import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Info, ExternalLink, AlertTriangle, ListChecks, Lightbulb, PenLine, ArrowRight } from "lucide-react";
import AdSlot from "@/components/AdSlot";

const CAT_LABELS = {
  "aprender-aleman": "Aprender alemán", "trabajo": "Trabajo", "vivienda": "Vivienda",
  "primeros-pasos": "Primeros pasos", "vida-cotidiana": "Vida cotidiana",
  "cultura-laboral": "Cultura laboral", "aleman-suizo": "Alemán suizo",
};

function Section({ s }) {
  return (
    <section className="mt-8">
      <h2 className="font-display font-bold text-2xl mb-3">{s.h}</h2>
      {Array.isArray(s.p) ? s.p.map((p, j) => <p key={j} className="leading-relaxed text-muted-foreground mb-3">{p}</p>)
                          : <p className="leading-relaxed text-muted-foreground">{s.p}</p>}
      {s.list && (
        <ul className="mt-3 space-y-2">
          {s.list.map((li, j) => (
            <li key={j} className="flex gap-2 text-sm"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />{li}</li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ExerciseBlock({ ex }) {
  const [show, setShow] = useState(false);
  return (
    <section className="mt-10 rounded-lg border border-accent/40 bg-accent/5 p-5">
      <div className="flex items-center gap-2 mb-3"><PenLine size={16} className="text-accent" /><h3 className="font-display font-semibold">Ejercicio breve</h3></div>
      <p className="font-medium">{ex.q}</p>
      {ex.options && (
        <ul className="mt-3 space-y-1.5">
          {ex.options.map((o, i) => <li key={i} className="text-sm text-muted-foreground">· {o}</li>)}
        </ul>
      )}
      <button onClick={() => setShow(!show)} className="mt-3 text-sm text-accent underline" data-testid="guide-exercise-reveal">
        {show ? "Ocultar solución" : "Ver solución"}
      </button>
      {show && (
        <div className="mt-2 rounded-md bg-card p-3 text-sm">
          <p className="font-semibold">Respuesta: {ex.answer}</p>
          {ex.explanation && <p className="text-muted-foreground mt-1">{ex.explanation}</p>}
        </div>
      )}
    </section>
  );
}

export default function GuideBody({ guide: g, showAds = false }) {
  return (
    <>
      {g.image && (
        <div className="mb-8 overflow-hidden rounded-lg border border-border bg-secondary">
          <img src={g.image} alt={g.image_alt || g.title} loading="lazy"
            className="w-full h-auto aspect-[3/2] object-cover" data-testid="guide-hero-image" />
        </div>
      )}
      <span className="text-xs uppercase tracking-[0.2em] text-accent">{CAT_LABELS[g.category] || g.category}</span>
      <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-3">{g.title}</h1>
      {g.subtitle && <p className="mt-3 text-base md:text-lg text-muted-foreground">{g.subtitle}</p>}

      <div className="mt-5 flex flex-wrap items-center gap-4 text-xs text-muted-foreground border-y border-border py-3">
        {g.reading_min && <span>{g.reading_min} min de lectura</span>}
        <span>Autor: {g.author_slug ? <Link to={`/autores/${g.author_slug}`} className="text-accent hover:underline" data-testid="author-link">{g.author}</Link> : (g.author || "Pendiente de asignar")}</span>
        {g.reviewer && <span>Revisor: {g.reviewer}</span>}
        {g.updated_at && <span>Actualizado: {new Date(g.updated_at).toLocaleDateString("es")}</span>}
      </div>

      <div className="mt-8 rounded-md bg-secondary p-4 text-sm flex gap-2">
        <Info size={16} className="text-accent shrink-0 mt-0.5" />
        <p className="text-muted-foreground">Información general con fines educativos. Puede cambiar y no constituye asesoramiento legal, fiscal, migratorio ni médico. Consulta el organismo oficial competente.</p>
      </div>

      {g.toc?.length > 0 && (
        <nav className="mt-8 rounded-lg border border-border p-5" aria-label="Tabla de contenidos">
          <div className="flex items-center gap-2 mb-3"><ListChecks size={16} className="text-primary" /><h2 className="font-display font-semibold text-sm">En esta guía</h2></div>
          <ol className="space-y-1.5 text-sm text-muted-foreground list-decimal pl-5">
            {g.toc.map((t, i) => <li key={i}>{t}</li>)}
          </ol>
        </nav>
      )}

      {g.body_intro && <p className="mt-8 text-lg leading-relaxed">{g.body_intro}</p>}

      {g.sections?.slice(0, 1).map((s, i) => <Section key={i} s={s} />)}

      {showAds && <AdSlot label="Publicidad" />}

      {g.sections?.slice(1).map((s, i) => <Section key={i} s={s} />)}

      {g.example && (
        <section className="mt-10 rounded-lg bg-secondary p-5">
          <div className="flex items-center gap-2 mb-2"><Lightbulb size={16} className="text-primary" /><h3 className="font-display font-semibold text-sm">Ejemplo</h3></div>
          <p className="font-medium">{g.example.de}</p>
          <p className="text-sm text-muted-foreground">{g.example.es}</p>
        </section>
      )}

      {g.exercise && <ExerciseBlock ex={g.exercise} />}

      {g.common_mistakes?.length > 0 && (
        <section className="mt-10 rounded-lg border border-amber-500/40 bg-amber-500/5 p-5">
          <div className="flex items-center gap-2 mb-2"><AlertTriangle size={16} className="text-amber-600" /><h3 className="font-display font-semibold text-sm">Errores frecuentes</h3></div>
          <ul className="space-y-1.5 text-sm text-muted-foreground list-disc pl-5">
            {g.common_mistakes.map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </section>
      )}

      {g.faq?.length > 0 && (
        <section className="mt-10">
          <h2 className="font-display font-bold text-2xl mb-4">Preguntas frecuentes</h2>
          {g.faq.map((f, i) => <div key={i} className="mb-4"><p className="font-semibold">{f.q}</p><p className="text-muted-foreground">{f.a}</p></div>)}
        </section>
      )}

      {g.sources?.length > 0 && (
        <section className="mt-10 rounded-lg border border-border p-5">
          <h3 className="font-display font-semibold mb-1">Fuentes</h3>
          {g.sources_checked && <p className="text-xs text-muted-foreground mb-3">Última comprobación de fuentes: {g.sources_checked}</p>}
          <ul className="space-y-2 text-sm text-muted-foreground">
            {g.sources.map((s, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <ExternalLink size={13} className="mt-0.5 shrink-0 text-accent" />
                {s.url ? (
                  <a href={s.url} target="_blank" rel="noopener noreferrer nofollow" className="text-accent hover:underline break-words" data-testid="guide-source-link">
                    {s.label}{s.note ? ` — ${s.note}` : ""}
                  </a>
                ) : (
                  <span>{s.label}{s.note ? ` — ${s.note}` : ""}</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {g.internal_links?.length > 0 && (
        <section className="mt-8">
          <h3 className="font-display font-semibold mb-3">Sigue aprendiendo</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {g.internal_links.map((l, i) => (
              <Link key={i} to={`/guias/${l[0]}`} data-testid="guide-internal-link"
                className="flex items-center justify-between rounded-md border border-border px-4 py-3 text-sm font-medium hover:border-primary/50 hover:bg-secondary transition-colors">
                {l[1]}<ArrowRight size={14} className="text-primary" />
              </Link>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
